import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import Button from '../Button/Button';
import { Form, FormInput } from '../Form/Form';

import './Modal.scss';

export interface ModalProps {
  backdrop?: boolean;
  className?: string;
  contained?: boolean;
  content?: JSX.Element;
  confirmationString?: string;
  description?: string;
  disableSave?: boolean;
  fixed?: boolean;
  footerAdditions?: JSX.Element;
  hideActionIcons?: boolean;
  hideFooter?: boolean;
  notDraggable?: boolean;
  noTransition?: boolean;
  saveText?: string;
  show: boolean;
  targetContainer?: HTMLElement;
  title?: string;
  onHide?: () => void;
  onSave?: () => void;
}

const Modal: FC<ModalProps> = (props) => {
  const nodeRef = useRef(null);

  const {
    backdrop,
    children,
    className,
    confirmationString,
    contained,
    content,
    description,
    disableSave,
    fixed,
    footerAdditions,
    hideActionIcons,
    hideFooter,
    notDraggable,
    noTransition,
    saveText,
    show,
    targetContainer,
    title,
    onHide,
    onSave,
  } = props;

  const hasChildren = !!content || !!children;
  const [localDisableSave, setLocalDisableSave] = useState(!!disableSave);
  const [confirmationFormValue, setConfirmationFormValue] = useState('');

  const handleHide = (event?: MouseEvent) => {
    event?.stopPropagation();
    setConfirmationFormValue('');
    if (onHide) {
      onHide();
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
    handleHide();
  };

  const handleConfirmationChange = (event) => {
    setConfirmationFormValue(event.target.value);
  };

  useEffect(() => {
    if (confirmationString) {
      // As an extra precaution, only allow exact value (all uppercase)
      if (confirmationFormValue === confirmationString?.toUpperCase()) {
        setLocalDisableSave(!!disableSave);
      } else {
        setLocalDisableSave(true);
      }
    } else {
      setLocalDisableSave(!!disableSave);
    }
  }, [confirmationFormValue, disableSave]);

  const modal = (
    <div
      ref={nodeRef}
      className={classNames('Modal', className, { contained })}
      tabIndex={-1}
      role="dialog"
    >
      <div className={classNames('Modal-dialog')} role="document">
        <div className="Modal-wrapper">
          <div className="Modal-body">
            {!hideActionIcons && (
              <div
                className={classNames('top-bar', {
                  'not-draggable': notDraggable,
                })}
              >
                {!fixed && (
                  <div className={classNames('icon', 'drag')}>
                    <i className="material-icons">open_with</i>
                  </div>
                )}
                <div onClick={handleHide} className={classNames('icon', 'close-icon')}>
                  <i className="material-icons">close</i>
                </div>
              </div>
            )}
            {title && <div className="header">{title}</div>}
            {description && <div className="description">{description}</div>}
            {title && <div className="gap" />}
            {hasChildren && (
              <div className="Modal-content">
                {content}
                {children}
              </div>
            )}
            {confirmationString && (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <FormInput
                  fieldName="confirmation-input"
                  label={`Type "${confirmationString}" below to confirm.`}
                  onChange={(event) => handleConfirmationChange(event)}
                  value={confirmationFormValue}
                />
              </Form>
            )}
            {!hideFooter && (
              <div className="footer d-flex justify-content-end">
                {footerAdditions && <div className="Modal-footer-additions">{footerAdditions}</div>}
                <Button
                  btnStyle="primary"
                  onClick={() => handleSave()}
                  className={classNames('ModalSubmitButton', { disabled: !onSave })}
                  disabled={!onSave || localDisableSave}
                >
                  {saveText || confirmationString || 'SAVE'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  let wrapper = modal;
  if (!notDraggable) {
    wrapper = (
      <Draggable nodeRef={nodeRef} handle=".top-bar" cancel=".close">
        {modal}
      </Draggable>
    );
  }
  if (!notDraggable && contained) {
    wrapper = (
      <div className="Modal-container">
        <Draggable nodeRef={nodeRef} handle=".Modal-dialog" cancel=".close" bounds="parent">
          {modal}
        </Draggable>
      </div>
    );
  }

  let portalChild = (
    <>
      <div className={classNames('Modal-backdrop', { dark: backdrop })} />
      {wrapper}
    </>
  );
  if (!noTransition) {
    portalChild = (
      <>
        <CSSTransition nodeRef={nodeRef} in={show} timeout={300} unmountOnExit classNames="fade">
          <div className={classNames('Modal-backdrop', { dark: backdrop })} />
        </CSSTransition>
        <CSSTransition nodeRef={nodeRef} in={show} timeout={300} unmountOnExit classNames="fade">
          {wrapper}
        </CSSTransition>
      </>
    );
  }

  // createPortal is used here to add the Modal
  return ReactDOM.createPortal(portalChild, targetContainer || document.body);
};

export default Modal;
